/**
 * DisabledReason Component
 * 
 * Displays user-friendly explanations for why an action is disabled.
 * Maps policy decision reason codes to helpful messages and icons.
 * 
 * Usage:
 * 
 * Basic:
 *   <DisabledReason decision={decision} />
 * 
 * Minimal (just icon + short text):
 *   <DisabledReason decision={decision} style="minimal" />
 * 
 * Block (full message with hints):
 *   <DisabledReason decision={decision} style="block" showHints />
 */

import React from 'react';
import {
  Lock,
  Clock,
  ShieldOff,
  UserX,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Snowflake,
  Shield,
  Check,
  Info,
  type LucideIcon,
} from 'lucide-react';
import type { PolicyDecision, PolicyReason } from '../../lib/authorization/types';
import {
  formatReasonMessage,
  getRemediationHints,
  getReasonIcon,
  getReasonColor,
  type RemediationHint,
} from '../../lib/authorization/reasonMessages';

// ============================================================================
// Props
// ============================================================================

export interface DisabledReasonProps {
  /** The policy decision to display */
  decision: PolicyDecision;
  
  /** Display style */
  style?: 'inline' | 'minimal' | 'block' | 'banner';
  
  /** Show remediation hints */
  showHints?: boolean;
  
  /** Show all reasons (not just primary) */
  showAllReasons?: boolean;
  
  /** Custom class name */
  className?: string;
  
  /** Optional callback when a hint action is clicked */
  onHintAction?: (hint: RemediationHint) => void;
}

// ============================================================================
// Icon Mapping
// ============================================================================

const ICON_MAP: Record<string, LucideIcon> = {
  Lock,
  Clock,
  ShieldOff,
  UserX,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Snowflake,
  Shield,
  Check,
  Info,
};

function getIconComponent(code: string): LucideIcon {
  const iconName = getReasonIcon(code);
  return ICON_MAP[iconName] || AlertTriangle;
}

// ============================================================================
// Color Mapping
// ============================================================================

const COLOR_CLASSES = {
  error: 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]',
  warning: 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]',
  info: 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]',
  success: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]',
};

const ICON_COLOR_CLASSES = {
  error: 'text-[var(--coral)]',
  warning: 'text-[var(--mustard)]',
  info: 'text-[var(--text-ter)]',
  success: 'text-[var(--green)] dark:text-[var(--text-ter)]',
};

// ============================================================================
// Component
// ============================================================================

export function DisabledReason({
  decision,
  style = 'inline',
  showHints = false,
  showAllReasons = false,
  className = '',
  onHintAction,
}: DisabledReasonProps): React.ReactElement {
  const primaryReason = decision.reasons[0];
  
  if (!primaryReason) {
    return (
      <span className={`text-[var(--text-ter)] ${className}`}>
        Action not available
      </span>
    );
  }
  
  const color = getReasonColor(primaryReason.code);
  const Icon = getIconComponent(primaryReason.code);
  
  // Minimal style - just icon and short code
  if (style === 'minimal') {
    return (
      <span 
        className={`inline-flex items-center gap-1 text-xs ${ICON_COLOR_CLASSES[color]} ${className}`}
        title={formatReasonMessage(primaryReason)}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    );
  }
  
  // Inline style - icon and message
  if (style === 'inline') {
    return (
      <span 
        className={`inline-flex items-center gap-1.5 text-sm ${ICON_COLOR_CLASSES[color]} ${className}`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>{formatReasonMessage(primaryReason)}</span>
      </span>
    );
  }
  
  // Banner style - horizontal alert bar
  if (style === 'banner') {
    return (
      <div 
        className={`flex items-center gap-2 px-3 py-2  border ${COLOR_CLASSES[color]} ${className}`}
        role="alert"
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="text-sm">{formatReasonMessage(primaryReason)}</span>
      </div>
    );
  }
  
  // Block style - full message with optional hints
  const hints = showHints ? getRemediationHints(primaryReason.code) : [];
  
  return (
    <div 
      className={` border p-4 ${COLOR_CLASSES[color]} ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        
        <div className="flex-1 space-y-2">
          {/* Primary reason */}
          <p className="text-sm font-medium">
            {formatReasonMessage(primaryReason)}
          </p>
          
          {/* Additional reasons */}
          {showAllReasons && decision.reasons.length > 1 && (
            <ul className="text-xs space-y-1 opacity-80">
              {decision.reasons.slice(1).map((reason, index) => (
                <li key={index} className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-current" />
                  {formatReasonMessage(reason)}
                </li>
              ))}
            </ul>
          )}
          
          {/* Remediation hints */}
          {hints.length > 0 && (
            <div className="pt-2 border-t border-current/20">
              <p className="text-xs font-medium opacity-70 mb-1">What you can do:</p>
              <ul className="text-xs space-y-1">
                {hints.map((hint) => (
                  <li key={hint.key} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
                    <span>{hint.message}</span>
                    {hint.actionLabel && onHintAction && (
                      <button
                        type="button"
                        className="underline hover:no-underline"
                        onClick={() => onHintAction(hint)}
                      >
                        {hint.actionLabel}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Compact Variants
// ============================================================================

/**
 * A simple tooltip-style reason display.
 */
export function DisabledReasonTooltip({ 
  decision 
}: { 
  decision: PolicyDecision 
}): React.ReactElement {
  const primaryReason = decision.reasons[0];
  const message = primaryReason ? formatReasonMessage(primaryReason) : 'Action not available';
  
  return (
    <div className="px-2 py-1 text-xs bg-[var(--bg-raised)] text-white rounded shadow-lg max-w-xs">
      {message}
    </div>
  );
}

/**
 * A badge-style reason indicator.
 */
export function DisabledReasonBadge({ 
  decision,
  className = '',
}: { 
  decision: PolicyDecision;
  className?: string;
}): React.ReactElement {
  const primaryReason = decision.reasons[0];
  const color = getReasonColor(primaryReason?.code ?? '');
  const Icon = getIconComponent(primaryReason?.code ?? '');
  
  const badgeColors = {
    error: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    warning: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    info: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    success: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeColors[color]} ${className}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {primaryReason?.code?.replace(/_/g, ' ').toLowerCase() || 'blocked'}
    </span>
  );
}

// ============================================================================
// List Component
// ============================================================================

/**
 * Display a list of reasons (for debugging or admin views).
 */
export function DisabledReasonList({ 
  reasons,
  className = '',
}: { 
  reasons: PolicyReason[];
  className?: string;
}): React.ReactElement {
  if (reasons.length === 0) {
    return <span className="text-[var(--text-ter)] text-sm">No reasons</span>;
  }
  
  return (
    <ul className={`space-y-1 ${className}`}>
      {reasons.map((reason, index) => {
        const Icon = getIconComponent(reason.code);
        const color = getReasonColor(reason.code);
        
        return (
          <li key={index} className="flex items-center gap-2 text-sm">
            <Icon className={`h-4 w-4 ${ICON_COLOR_CLASSES[color]}`} aria-hidden="true" />
            <span>{formatReasonMessage(reason)}</span>
            {reason.data && Object.keys(reason.data).length > 0 && (
              <code className="text-xs bg-[var(--hover-tint)] px-1 rounded">
                {JSON.stringify(reason.data)}
              </code>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default DisabledReason;
